import { useHasPermission, useIsStaff, useIsSuperUser } from '@/features/access-control';
import { useUserInfo } from '@/features/space-user';
import { useCompaniesOptions, useDelegationOptions } from '@/shared/api';
import { PERMISSIONS } from '@/shared/constants';
import type { Permission } from '@/shared/constants';
import { resolveCompanySwitchCapabilities } from '../domain/capabilities';

// Moved here from the now-deleted `features/topbar/hooks/useTopBarAccess.ts` — the settings
// gate belongs with the switch's own gear now, not with the topbar dropdown.
export const SETTINGS_PERMISSIONS = [
	PERMISSIONS.EMPLOYEE_READ,
	PERMISSIONS.DELEGATION_READ,
	PERMISSIONS.FISCAL_DATA_READ,
	PERMISSIONS.SETTING_CURRENCY,
	PERMISSIONS.SETTING_REFUNDS,
	PERMISSIONS.ERP_READ,
] as const satisfies readonly Permission[];

/**
 * Shared scope + capability assembly for both the sidebar card and the switch modal — one set
 * of React Query calls (deduped by TanStack Query's cache across both consumers) feeding the
 * pure capability rules.
 */
export const useCompanySwitchScope = () => {
	const { data: userInfo } = useUserInfo();
	const isSuperUser = useIsSuperUser();
	// `isStaff` — a Frekuent operator scoped like a super admin but limited to their own
	// companies and role — is decoded from the login token's own claim.
	const isStaff = useIsStaff();

	const { hasPermission: hasDelegationAccessPermission } = useHasPermission(
		PERMISSIONS.DELEGATION_FAKE_ACCESS,
	);
	const { hasPermission: hasSettingsAccess } = useHasPermission([...SETTINGS_PERMISSIONS], 'any');

	const { data: companies, isLoading: isLoadingCompanies } = useCompaniesOptions();
	// Only fire the delegations query for users who can actually act on it — this endpoint is
	// currently only hit when the switch renders, and the card mounts on every page.
	const { data: delegationsOptions, isLoading: isLoadingDelegations } = useDelegationOptions(
		undefined,
		{ enabled: hasDelegationAccessPermission },
	);

	const capabilities = resolveCompanySwitchCapabilities({
		isSuperUser,
		isStaff,
		companyCount: companies?.length ?? 0,
		delegationCount: delegationsOptions?.length ?? 0,
		hasDelegationAccessPermission,
	});

	return {
		userInfo,
		isSuperUser,
		isStaff,
		hasDelegationAccessPermission,
		hasSettingsAccess,
		companies,
		isLoadingCompanies,
		delegationsOptions,
		isLoadingDelegations,
		capabilities,
	};
};
